import type { Signal } from '@angular/core';

import type { AppPath, HttpsUrl, IconName, RouteBase, RouteInfo } from '@kernel';

export type QueryParams = Record<string, readonly string[]>;

export const NavMode = {
  Replace: 'REPLACE',
  Tab: 'TAB',
  Window: 'WINDOW',
} as const;

export type NavMode = (typeof NavMode)[keyof typeof NavMode];

export const NavIntent = {
  InPlace: 'IN_PLACE',
  TabSwitch: 'TAB_SWITCH',
} as const;

export type NavIntent = (typeof NavIntent)[keyof typeof NavIntent];

export interface NavContext {
  readonly intent: NavIntent | null;
  readonly tabId: string | null;
}

interface BaseMenuItem {
  readonly title: string; // i18n key or display text
  readonly icon?: IconName;
  readonly badge?: string; // i18n key or display text
  readonly count?: Signal<number>;
  readonly permission?: string;
}

export type MenuItem =
  | (BaseMenuItem & {
      readonly isSection: true;
      readonly children?: never;
      readonly path?: never;
      readonly url?: never;
    })
  | (BaseMenuItem & {
      readonly isSection?: false;
      readonly children: readonly MenuItem[];
      readonly path?: never;
      readonly url?: never;
    })
  | (BaseMenuItem & {
      readonly isSection?: false;
      readonly children?: never;
      readonly path: AppPath;
      readonly url?: never;
    })
  | (BaseMenuItem & {
      readonly isSection?: false;
      readonly children?: never;
      readonly path?: never;
      readonly url: HttpsUrl;
    });

export interface TabKey {
  readonly path: RouteBase;
  readonly queryParams: QueryParams;
  readonly fragment: string | null;
}

export interface TabBase extends RouteInfo {
  readonly key: string; // Root path, or path plus query and fragment for non-root tabs
  readonly isRoot: boolean;
}

export interface Tab extends TabBase {
  readonly id: string;
  readonly history: readonly TabBase[];
}

export interface ScrollOffset {
  readonly element: HTMLElement;
  readonly top: number;
  readonly left: number;
}

export const DEFAULT_TAB_TITLE = 'tab.newTab';
export const SCROLL_RESTORE_ATTEMPTS = 8;
